import { jsx } from 'react/jsx-runtime';
import React__default, { useState, useEffect, useCallback, useMemo } from 'react';
import { SuggestionItemType } from '../magic.types.js';
import { useQueryMagicSuggestions } from '../queries/useQueryMagicSuggestions.js';
import { createOptimisticPrivateTag } from '../../tags/utils/createOptimisticPrivateTag.js';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { simpleHash } from '@fabric-app/utils/text';
import noop from '../../../node_modules/lodash-es/noop.js';

const MagicSuggestionsContext = /* @__PURE__ */ React__default.createContext({
  listHash: null,
  suggestions: null,
  onSelectSuggestion: noop,
  popSuggestion: noop
});
const MagicSuggestionsContextProvider = ({ context: contextProp, onSelectSuggestion: onSelectSuggestionProp, includeFolders, keepPrevious = true, children }) => {
  const context = useDebounce(contextProp, 500);
  const { data: remoteSuggestions, isLoading: suggestionsLoading } = useQueryMagicSuggestions(context);
  const [listHash, setListHash] = useState(null);
  const [locked, setLocked] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  useEffect(() => {
    if (keepPrevious) return;
    setSuggestions([]);
  }, [
    context,
    keepPrevious
  ]);
  useEffect(() => {
    if (locked || !remoteSuggestions || suggestionsLoading) return;
    const tagsExisting = (remoteSuggestions?.tagsExisting || []).map((t) => ({
      ...t,
      type: SuggestionItemType.TAG,
      tag: t
    }));
    const tagsNew = (remoteSuggestions?.tagsNew || []).map((t) => {
      const optimisticTag = createOptimisticPrivateTag(t.name);
      return {
        ...t,
        id: optimisticTag.id,
        type: SuggestionItemType.TAG,
        tag: optimisticTag,
        draftReason: "suggestion"
      };
    });
    const folders = !includeFolders ? [] : (remoteSuggestions?.folders || []).map((t) => ({
      ...t,
      type: SuggestionItemType.FOLDER
    }));
    const tags = [
      ...tagsExisting,
      ...tagsNew
    ].slice(0, folders.length > 0 ? 2 : 3);
    const suggestionOptions = [
      ...tags,
      ...folders
    ].slice(0, 3);
    setSuggestions(suggestionOptions);
    setListHash(simpleHash(JSON.stringify(suggestionOptions)));
  }, [
    includeFolders,
    locked,
    remoteSuggestions,
    suggestionsLoading
  ]);
  const popSuggestion = useCallback((el) => {
    setLocked(true);
    setSuggestions((prev) => (prev ?? []).filter((s) => !((s.id ?? s.name) === (el.id ?? el.name) && s.type === el.type)));
  }, []);
  const onSelectSuggestion = useCallback(async (el) => {
    popSuggestion(el);
    onSelectSuggestionProp?.(el);
  }, [
    onSelectSuggestionProp,
    popSuggestion
  ]);
  const value = useMemo(() => {
    return {
      listHash,
      suggestions,
      onSelectSuggestion,
      popSuggestion
    };
  }, [
    listHash,
    onSelectSuggestion,
    suggestions,
    popSuggestion
  ]);
  return /* @__PURE__ */ jsx(MagicSuggestionsContext.Provider, {
    value,
    children
  });
};
const useMagicSuggestionsContext = () => React__default.useContext(MagicSuggestionsContext);

export { MagicSuggestionsContextProvider, useMagicSuggestionsContext };
//# sourceMappingURL=MagicSuggestionsContext.js.map
